import type { TimeFormat } from '@app/api/Branches/types';
import type { ModelRecordProduct } from '@app/api/Products/types';
import type { ModelRecordSubscriptions } from '@app/api/Subscriptions/types';
import type {
  ClientStatusEnum,
  RecordTypeEnum,
} from '@app/components/RecordModal/components/RecordDetails/types';
import type { SelectedPaymentMethod } from '@app/components/RecordModal/components/RecordPayment/types';
import type { Int32ItemsFilter } from '@app/pages/Clients/Records/utils/msToInt32ItemsFilters';
import type {
  ColorFactory,
  NClientPayments,
  NClients,
  NClientServices,
  NSchedule,
  NSmallGlobalEntities,
  NTags,
} from '@app/types';

import type { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import type { FormInstance } from 'antd/lib';
import type dayjs from 'dayjs';

export namespace NCalendar {
  export interface ClientsInfoProps {
    form: FormInstance;
    record?: IRecordType | null;
    fromRecords?: boolean;
    onChangePhoneValidationError?(error: string): void;
  }
  export type ClientsInfo = (args: ClientsInfoProps) => JSX.Element;

  export type Services = (args: { form: FormInstance }) => JSX.Element;

  export type EmployeeInfo = (args: { form: FormInstance }) => JSX.Element;

  export type ServiceFooter = (args: {
    serviceInfo: TServiceCost;
    getServiceTimeString: () => string;
    roundToTwo: (value: number) => number;
  }) => JSX.Element;

  export type TimeSidebarProps = {
    times: {
      id: string;
      value: string;
    }[];
    smallestTimeValue?: string | Date | null;
    columnRef?: React.RefObject<HTMLDivElement>;
    className: string;
    side: 'left' | 'right';
    onScroll: () => void;
  };

  export type EmptyCalendarProps = {
    times: string[];
    hasEmployee: boolean;
    className?: string;
  };
}

export namespace NRecords {
  export type ClientRecord = {
    Client: string | null;
    ClientId: number;
    ClientPhones: string | null;
    ClientServices: string | null;
    CreateUser: string | null;
    CreateUserId: number;
    Cost: string;
    Employee: string | null;
    EmployeeId: number;
    Id: number;
    Position: string | null;
    Status: string | null;
    Time: string;
    TimeCreated: string;
    Tags: NTags.Tag[] | null;
  };

  export type ModelRecordFilterLists = {
    Employees: ModelItem[] | null;
    Users: ModelItem[] | null;
    Tags: ModelItem[] | null;
    ClientTags: ModelItem[] | null;
    Services: ModelItem[] | null;
    ServiceCategories: (ModelItem & { IsDeleted: boolean })[] | null;
    AdditionalSales: ModelItem[] | null;
    RecordTypes: ModelItem[] | null;
  };

  export type ModelItem = {
    Id: number;
    Name: string | null;
    IsDeleted?: boolean;
    IdS?: string | null;
  };

  export type ModelRecordTip = {
    RecordType: string | null;
    TimeS: string;
    TimeE: string;
  };

  export type ModelClientRecordService = {
    ServiceId: number;
    Service: string | null;
    Count: number;
    Cost: number;
    Discount: number;
    CostWithDiscount: number;
    Paid: number;
  };

  export type ModelClientRecord = {
    RecordId: number;
    TimeE: string;
    TimeS: string;
    Status: NClients.Status | null;
    EmployeeId: number;
    Employee: string | null;
    Comment: string | null;
    IsUnpaid: boolean;
    Services: ModelClientRecordService[] | null;
  };

  export type ModelRecordsOnDates = {
    EmployeeId: number;
    DateS: string;
    DateE: string;
    RecordsOnDates: number[];
  };

  export type ModelRecordCheck = {
    BranchId: number;
    EmployeeId: number;
    TimeS: string;
    TimeE: string;
    IsWarning: boolean;
    IsError: boolean;
    Message: string | null;
  };

  export type ModelEmployeeByService = {
    UserId: number;
    NameLast: string | null;
    NameFirst: string | null;
    NameMiddle: string | null;
    Date: string;
    BranchId: number;
    IsWorking: boolean;
  };

  export type ModelRecordSearchResult = {
    Id: number;
    Filter: ModelRecordSearchFilter;
    Records: ModelRecordLite[] | null;
    PageN: number;
    CountPages: number;
    CountRecords: number;
  };

  export type ModelRecordSearchFilter = {
    SearchTerm: string | null;
    DateCreateS: string | null | dayjs.Dayjs;
    DateCreateE: string | null | dayjs.Dayjs;
    DateVisitS: string | null;
    DateVisitE: string | null;
    EmployeeIds: number[] | null;
    UserIds: number[] | null;
    ServiceCategoryIds: number[] | null;
    ServiceIds: number[] | null;
    Statuses: NClients.Status[] | null;
    RecordsPerPage: number;
    AdditionalSaleIds: number[] | null;
    PaymentStatus: 'Paid' | 'Unpaid' | 'All';
    RecordTagsFilters: Int32ItemsFilter[] | null;
    ClientTagsFilters: Int32ItemsFilter[] | null;
    Types: string[] | null;
  };

  export type ModelRecordLite = {
    Id: number;
    Time: string;
    EmployeeId: number;
    TimeCreated: string;
    CreateUserId: number;
    CreateUser: string | null;
    Employee: string | null;
    Position: string | null;
    ClientId: number;
    Client: string | null;
    ClientPhones: string | null;
    ClientServices: string | null;
    Status: string | null;
    RecordColor: string | null;
    Cost: number | null;
  };

  export type ModelRecordsTags = {
    RecordIds: number[] | null;
    TagIds: number[] | null;
  };

  export type ModelRecordMoveCheck = {
    RecordId: number;
    MoveTimeS: string;
    MoveTimeE: string;
    MoveEmployeeId: number;
    CanMove: boolean;
    Message: string | null;
  };

  export type ModelRecord = {
    Id: number;
    BranchId: number;
    TimeS: string;
    TimeE: string;
    ClientId: number;
    NameLast: string | null;
    NameFirst: string | null;
    Email: string | null;
    Phone: string | null;
    IsPhoneVerified: boolean;
    PhoneCountryId: number | null;
    ClientServices: NClientServices.ModelClientServiceLite[] | null;
    Products?: ModelRecordProduct[] | null;
    /** Абонементы, проданные через запись (GetRecordById) */
    Subscriptions?: ModelRecordSubscriptions | null;
    Status: NClients.Status | null;
    Comment: string | null;
    EmployeeId: number | null;
    IsEmployeeLocked: boolean;
    IsDeleted: boolean;
    Tags: NTags.ModelTag[] | null;
    IsFirstVisit: boolean;
    OtherRecords: OtherRecordsInterface[] | null;
    MigrateId: number | null;
    MigrateClientId: number | null;
    IsPaid: boolean;
    IsCostZero: boolean;
    IsNoNotify: boolean;
    RecordColor: string | null;
    ClientFutureRecordIds: number[] | null;
    EmployeePayoff: number | null;
    IsLoyaltyApplied: boolean;
  };

  export type ModelEmployeeRecords = {
    UserId: number;
    NameLast: string | null;
    NameFirst: string | null;
    NameMiddle: string | null;
    NameFull: string | null;
    Photo: string | null;
    PositionId: number | null;
    Position: string | null;
    IsDeleted: boolean;
    Date: string;
    // TODO: доработать типы этих свойств позднее в рамках task/3444,
    // в консоли при их изменении появляется >20 ошибок
    WorkSchedules: NSchedule.ModelWorkSchedule[] | null;
    WorkBreaks: NSchedule.ModelWorkBreak[];
    Records: IRecordType[];
  };

  export type VisitHistoryRecords = Record<
    string,
    NRecords.ModelClientRecord[]
  >;

  export type VisitHistoryComponentRecords = {
    records: NRecords.VisitHistoryRecords | null;
  };
}

export interface IRecordType {
  BranchId: number;
  ClientId: number;
  ClientServices: TClientService[];
  Comment: string;
  Duration: number;
  Email: string;
  EmployeeId: number;
  Id: number;
  IsDeleted: boolean;
  IsEmployeeLocked: boolean;
  IsFirstVisit: boolean;
  IsPhoneVerified: boolean;
  NameFirst: string;
  NameLast: string;
  Phone: string;
  PhoneCountryId: number;
  PhoneCode: string;
  Status: NClients.Status | null;
  Tags: TTag[];
  TimeE: string;
  TimeS: string;
  col?: HTMLElement | null;
  distanceBeetling?: number;
  endNode?: HTMLElement | null;
  node?: HTMLElement | null;
  recordId: number;
  rowIndex: number;
  startDate: string;
  OtherRecords: OtherRecordsInterface[];
  IsPaid: boolean;
  cascade?: number;
  startRect?: DOMRect | null;
  endRect?: DOMRect | null;
  ClientsIdGroup?: number;
  RecordColor: string;
  ClientFutureRecordIds: number[] | null;
  EmployeePayoff: number | null;
  RecordType?: RecordTypeEnum | null;
  ClientHasLoyaltyCard?: boolean;
  IsLoyaltyApplied?: boolean;
}

export interface EmployeeInterface {
  // Серверные свойства
  UserId: number;
  NameLast: string;
  NameFirst: string;
  NameMiddle: string;
  NameFull?: string;
  Photo: string;
  PositionId: number;
  Position: string;
  IsDeleted?: boolean;
  Date?: string;
  WorkSchedules: NSchedule.ModelWorkSchedule[];
  WorkBreaks: NSchedule.ModelWorkBreak[];
  Records: RecordInterface[];
  Id: number;
}

export interface UpdatedEmployeeInterface extends EmployeeInterface {
  label?: string;
  value?: number;
  TimeS?: string;
  allTimeSchedules?: TAllTimeSchedule[];
}

export interface ClientServicesInterface {
  Id: number;
  Name: string;
  CategoryId: number;
  Category: string;
  PriceS: number;
  PriceE: number;
  Duration: number;
  Count: number;
  UserId: number;
  Discount: number;
  Paid: number;
  IsDeleted: boolean;
  Discounts?: ModelRecordServiceDiscount[] | null;
  IsExcludedFromLoyaltyDiscount: boolean;
  /** Услуга с абонемента клиента (ТЗ us/11427) */
  ClientSubscriptionId?: number | null;
}

export interface TagInterface {
  Id: number;
  Name: string;
  Color: string;
  IconId: string;
  Description: string;
  IsDeleted: boolean;
  IsSelected: boolean;
  IsSystem: boolean;
  SystemId: string;
}

export interface OtherRecordsInterface {
  Id: number;
  TimeS: string;
  TimeE: string;
  EmployeeId: number;
  NameFirst: string;
  NameLast: string;
  NameMiddle: string;
  Status: NClients.Status | null;
}

export interface ClientServiceInterface {
  Id: number;
  Name: string | null;
  CategoryId: number | null;
  Category: string | null;
  PriceS: number | null;
  PriceE: number | null;
  Duration: number | null;
  Count: number | null;
  UserId: number | null;
  Discount: number | null;
  Paid: number | null;
  Discounts?: ModelRecordServiceDiscount[] | null;
}

export interface ClientServicesRequestInterface {
  Discount: number;
  Count: number;
  Duration: number;
  PriceS: number;
  UserId: number | null;
  Id?: number | null;
  Name?: string | null;
  CategoryId?: number | null;
  Category?: string | null;
  PriceE?: number | null;
  Paid?: number | null;
  /** Услуга с абонемента клиента (ТЗ us/11427) */
  ClientSubscriptionId?: number | null;
}
export interface UpdateRecordDataInterface {
  Id: number;
  ClientId: number;
  PhoneCountryId: number;
  BranchId: number | null;
  EmployeeId: number | null;
  IsEmployeeLocked: boolean;
  IsDeleted: boolean;
  Tags: {
    id: number;
  }[];
  RecordColor: string | null;
  TimeS: string;
  TimeE: string;
  NameLast: string;
  NameFirst: string;
  Email?: string;
  Phone: string;
  IsPhoneVerified: boolean;
  Status: ClientStatusEnum;
  Duration: number;
  Comment: string;
  Color: string | null;
  ClientServices: ClientServicesRequestInterface[];
  Products?: ModelRecordProduct[];
  // Только при редактировании: тип новой записи считает бэк (issue/11348).
  RecordType?: RecordTypeEnum | null;
  EmployeePayoff: number | null;
}
export interface RecordInterface {
  Id: number;
  BranchId: number;
  TimeS: string;
  TimeE: string;
  ClientId: number | null;
  NameLast: string;
  NameFirst: string;
  NameMiddle: string;
  Email: string;
  Phone: string;
  IsPhoneVerified: boolean;
  PhoneCountryId: number;
  ClientServices: Array<ClientServicesInterface>;
  Products?: ModelRecordProduct[] | null;
  /** Абонементы, проданные через запись (GetRecordById) */
  Subscriptions?: ModelRecordSubscriptions | null;
  Status: NClients.Status | null;
  Comment: string;
  EmployeeId: number;
  IsEmployeeLocked: boolean;
  IsDeleted: boolean;
  Tags: Array<TagInterface>;
  IsFirstVisit: boolean;
  OtherRecords: Array<OtherRecordsInterface>;
  MigrateId: number;
  MigrateClientId: number;
  IsPaid: boolean;
  IsCostZero: boolean;
  IsNoNotify: boolean;
  RecordColor: string | null;
  ClientFutureRecordIds: Array<number>;
  RecordType?: RecordTypeEnum | null;
  EmployeePayoff: number | null;
  IsFullyPaid: boolean;
  LeadId: number;
  IsLoyaltyApplied: boolean;
  IsExcludedFromLoyaltyDiscount: boolean;
}

export type TServiceCost = {
  priceS: number;
  priceE?: number;
  duration: number;
  count: number;
  discount: number;
};

export type TPaymentAccount = {
  Id: number;
  BranchId: number;
  LegalEntityId: number;
  LegalEntity: string;
  Name: string;
};

export type TRecordService = {
  ClientService: string;
  ClientServiceId: number;
  Cost: number;
  Count: number;
  Discount: number;
  Discounts: ModelRecordServiceDiscount[] | null;
  Id: number;
  Paid: number;
  Price: number;
  TotalDiscount: number;
  IsExcludedFromLoyaltyDiscount: boolean;
};

export interface ModelRecordServiceDiscount {
  Discount: number;
  Type: string | null;
}

export interface ModelRecordProductDiscount {
  Discount: number;
  Type: string | null;
}

export type TFrontRecordService = {
  key: TRecordService['ClientServiceId'] | string;
  position: TRecordService['ClientService'] | JSX.Element;
  count: TRecordService['Count'] | string | JSX.Element;
  cost: TRecordService['Cost'] | string | JSX.Element;
  discount: TRecordService['Discount'] | string | JSX.Element;
  total: TRecordService['Cost'] | string | JSX.Element;
  paid: TRecordService['Paid'] | string | JSX.Element;
  balance: number | string | JSX.Element;
};

export type TPaymentInfoData = {
  Balance: NClientPayments.ModelBalance;
  PaymentAccounts: TPaymentAccount[];
  RecordId: number;
  RecordServices: TRecordService[];
  RecordProducts?: ModelRecordProduct[] | null;
  TotalCost: number;
  TotalDiscount: number;
  TotalPaid: number;
  RecordStatus: string | null;
};

export type TModelRecordReceipt = {
  RecordId: number;
  RecordServices: TRecordService[];
  RecordProducts?: ModelRecordProduct[] | null;
  TotalDiscount: number;
  TotalCost: number;
  TotalPaid: number;
  Balance: NClientPayments.ModelBalance;
  PaymentAccounts: TPaymentAccount[];
  RecordStatus: string | null;
  // TODO: в рамках task/3444 убедиться,
  // что свойство RecordIds может быть опциональным
  RecordIds?: number[] | null;
};

export type TClientBalanceData = {
  ObjectId: string;
  Balance: string;
};

export type TPaymentInfoProps = {
  id: number;
  clientId: number;
  recordIds: number[];
  handleOptionClick: (
    paymentModalId: number,
    receipt?: SelectedPaymentMethod | null
  ) => void;
  dataM: TModelRecordReceipt | null;
  setDataM: React.Dispatch<React.SetStateAction<TModelRecordReceipt | null>>;
  clientBalanceData: TClientBalanceData;
  setClientBalanceData: React.Dispatch<
    React.SetStateAction<TClientBalanceData>
  >;
  setPaymentAmount: React.Dispatch<React.SetStateAction<string>>;
  paymentAmount: string;
  updatedTableData: TFrontRecordService[];
  cost: { count: number } | undefined;
  setUpdatedTableData: (data: TFrontRecordService[]) => void;
  updateReceipt: (cost: { count: number }, dataM: TModelRecordReceipt) => void;
};

export type TVisitsHistoryProps = {
  clientId: number;
};

export type TModalFormProps = {
  form: FormInstance;
  onSubmit: (values: TModalSubmitValues) => void;
  record: IRecordType | null;
  errorText: string;
  setErrorText: React.Dispatch<React.SetStateAction<string>>;
  onChangePhoneValidationError?(error: string): void;
  toPaymentInfo(): void;
};

export type TEmployeeOptions = {
  label: string;
  value: number;
};

export type TGroupedRecords = {
  [key: number]: IRecordType[];
};

export interface TBranchWorkTimes {
  WorkTimeS: string;
  WorkTimeE: string;
}

export interface BranchWorkingHoursForDate extends TBranchWorkTimes {
  Timezone: NSchedule.BranchWorkTimeInterface['Timezone'];
  BranchId: number;
  Date: string;
  JournalFormatTime: TimeFormat;
}

export type TAllTimeSchedule = {
  TimeS: string;
  TimeE: string;
  isWorkingTime: boolean;
  times?: string[];
};

export type TWorkBreak = {
  Id: number;
  UserId: number;
  BranchId: number;
  TimeS: string;
  TimeE: string;
};

export type TEmployee = {
  // Серверные свойства
  UserId: number;
  NameLast: string;
  NameFirst: string;
  NameMiddle: string;
  NameFull?: string;
  Photo: string;
  PositionId: number;
  Position: string;
  IsDeleted?: boolean;
  Date?: string;
  WorkSchedules: NSchedule.ModelWorkSchedule[];
  WorkBreaks: NSchedule.ModelWorkBreak[];
  Records: IRecordType[];

  // Фронтовые свойства
  label?: string;
  value?: number;
  TimeS?: string;
  Id?: number;
  allTimeSchedules?: TAllTimeSchedule[];
};

export type TEmployeeDetailsProps = {
  times: Array<{ id: string; value: string }>;
  employeesData: TEmployee[];
  rowRef?: React.MutableRefObject<HTMLDivElement>;
  onActiveTimeClick: (
    event: React.MouseEvent<Element, MouseEvent>,
    employee: TEmployee
  ) => void;
  columnsRef?: React.MutableRefObject<HTMLDivElement>;
};

export type TEmployeeColumnProps = {
  calendarTimeBounds: string[];
  employee: TEmployee;
  employeesCount: number;
  verticalScrollContainerRef: React.RefObject<HTMLDivElement>;
  columnsRef?: React.MutableRefObject<Record<
    string,
    Record<string, number>
  > | null>;
  rowRef?: React.RefObject<HTMLDivElement>;
  onActiveTimeClick: (
    event: React.MouseEvent<Element, MouseEvent>,
    employee: TEmployee
  ) => void;
  onDragRecordStart: (
    event: React.DragEvent<HTMLDivElement>,
    initialTop: number,
    record: IRecordType
  ) => void;
  onDragOver: (event: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (event: React.DragEvent<HTMLDivElement>, employee: TEmployee) => void;
  onWorkBreakClick: (workBreak: TWorkBreak, employee: TEmployee) => void;
  times: Array<{ id: string; value: string }>;
  timeFormat: TimeFormat | null;
};

export type TRecordProps = {
  calendarTimeBounds: string[];
  record: IRecordType;
  employeeData: TEmployee;
  onDragRecordStart: (
    event: React.DragEvent<HTMLDivElement>,
    initialTop: number,
    record: IRecordType
  ) => void;
  timeFormat: TimeFormat | null;
};

export type TOtherRecord = {
  Id: number;
  TimeS: string;
  TimeE: string;
  EmployeeId: number;
  Employee: string;
};

export type TTimePeriod = {
  label: string;
  value: string;
};

export type TClientStatuses = {
  status: NClients.Status;
  label: string;
  color: string;
  icon: IconDefinition;
};

export type TTime = {
  hours: number;
  minutes: number;
  duration: number;
};

export type TClientService = {
  Id?: number;
  Name?: string;
  PriceS?: number | null;
  PriceE?: number | null;
  Duration?: number;
  Count?: number;
  UserId?: number | null;
  Discount?: number;
  initialDuration?: number;
  discount?: number;
};

export type TTag = {
  Id: number;
  Name: string;
  Color: string;
  IconId: string;
  Description: null | string;
  IsDeleted: boolean;
  IsSelected: boolean;
  IsSystem: boolean;
  SystemId?: string;
};

export type TEmployeeData = {
  LeadId: number | null;
  BranchId: number;
  Branch: string;
  EmployeeId: number;
  // Организация включила окно тегов после сохранения новой записи (us/11144).
  IsRecordTagsWindow: boolean;
  ClientServices: TClientService[];
  WorkSchedules: NSchedule.ModelWorkSchedule[];
  BranchUsers: TBranchUser[];
  BranchEmployees: TBranchUser[];
  Warehouses: NSmallGlobalEntities.ModelItem[] | null;
  Tags: TTag[];
};

export type TClientInfo = {
  NameFirst: string;
  NameLast: string;
  Email: string;
};

export type TBranchUser = {
  NameFirst: string | null;
  NameMiddle: string | null;
  NameLast: string | null;
  UserId: number;
};

export type TService = {
  Id: number;
  Name: string;
  CategoryId: number;
  Category: string;
  PriceE: number;
  PriceS: number;
  Duration: number;
  Count: number;
  UserId: number | null;
  Discount: number;
  Paid: number;
};

export type TModalSubmitValues = {
  Comment: string;
  Date: moment.Moment;
  Email: string;
  EmployeeId: number;
  Hour0: number;
  IsPhoneVerified: boolean;
  Minute0: number;
  NameFirst: string;
  NameLast: string;
  Phone: string;
  CountryId: number;
  TimeE: string;
  TimeS: string;
  tag: TTag | undefined;
  RecordColor: ColorFactory | string;
  Status: NClients.Status;
  AdditionalEmployees?: Array<number | null>;
};
